import React from 'react';
import { useLanguage } from './LanguageContext';

const translations = {
  en: {
    badge: 'Dead Simple',
    title: 'Three steps. Zero effort.',
    subtitle: 'We stripped away everything complicated about cooking. Here\'s what\'s left.',
    step1: {
      title: 'Open. Snap. Done.',
      description: 'Open your fridge door, hold up your phone, and tap. Our camera sees what you see — chicken, tomatoes, that leftover rice from yesterday. All recognized instantly.'
    },
    step2: {
      title: 'AI does the thinking.',
      description: 'In about 3 seconds, Chaiffy scans your ingredients, cross-references thousands of recipes, and picks the best one for tonight. It even considers what spices you probably have.'
    },
    step3: {
      title: 'You do the eating.',
      description: 'Follow along with clear, no-nonsense instructions. Exact measurements, perfect timing, and results that\'ll make everyone ask "where did you learn to cook like that?"'
    },
    cta: 'Give It a Shot'
  },
  ar: {
    badge: 'بسيط جداً',
    title: 'ثلاث خطوات. صفر مجهود.',
    subtitle: 'أزلنا كل شيء معقد في الطبخ. إليك ما تبقى.',
    step1: {
      title: 'افتح. صوّر. خلاص.',
      description: 'افتح باب الثلاجة، ارفع هاتفك، واضغط. كاميرتنا ترى ما تراه — دجاج، طماطم، الأرز المتبقي من أمس. كل شيء يُتعرف عليه فوراً.'
    },
    step2: {
      title: 'الذكاء الاصطناعي يفكر بدلاً عنك.',
      description: 'في حوالي 3 ثوانٍ، يمسح Chaiffy مكوناتك، يقارنها بآلاف الوصفات، ويختار الأفضل لليلة. حتى أنه يأخذ بعين الاعتبار التوابل الموجودة عندك.'
    },
    step3: {
      title: 'أنت تتكفل بالأكل.',
      description: 'تابع مع تعليمات واضحة ومباشرة. قياسات دقيقة، توقيت مثالي، ونتائج تجعل الجميع يسأل "من وين تعلمت تطبخ كذا؟"'
    },
    cta: 'جرّبه الحين'
  }
};

const HowItWorks = () => {
  const { language, isRTL } = useLanguage();
  const t = translations[language];

  const steps = [
    {
      ...t.step1,
      icon: '📸',
      accent: 'border-emerald-400',
      accentBg: 'bg-emerald-400',
      num: '1',
    },
    {
      ...t.step2,
      icon: '🧠',
      accent: 'border-blue-400',
      accentBg: 'bg-blue-400',
      num: '2',
    },
    {
      ...t.step3,
      icon: '😋',
      accent: 'border-amber-400',
      accentBg: 'bg-amber-400',
      num: '3',
    }
  ];

  return (
    <section id="how-it-works" className="section-padding bg-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-1.5 bg-dark text-white rounded-full text-xs font-bold uppercase tracking-widest mb-5">
            {t.badge}
          </span>
          <h2 className="heading-lg text-dark mb-4">
            {t.title}
          </h2>
          <p className="body-lg max-w-lg mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="space-y-6 md:space-y-0">
          {steps.map((step, index) => (
            <div key={index} className={`flex flex-col md:flex-row items-start gap-6 md:gap-10 ${index !== steps.length - 1 ? 'mb-12 md:mb-16' : ''} ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
              <div className={`flex-shrink-0 w-full md:w-72 h-48 md:h-56 rounded-3xl bg-gradient-to-br from-gray-50 to-gray-100 border-2 ${step.accent} flex flex-col items-center justify-center relative overflow-hidden group`}>
                <div className="text-5xl md:text-6xl mb-2 group-hover:scale-110 transition-transform duration-300">{step.icon}</div>
                <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} w-8 h-8 ${step.accentBg} rounded-full flex items-center justify-center`}>
                  <span className="text-white text-sm font-extrabold">{step.num}</span>
                </div>
              </div>

              <div className="flex-1 pt-2">
                <h3 className="text-2xl md:text-3xl font-bold text-dark mb-3 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-gray-500 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <a href="/start" className="btn-primary text-base md:text-lg">
            {t.cta} {isRTL ? '←' : '→'}
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
